import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFieldArray } from 'react-hook-form';
import { get } from 'lodash';

import { selectUserGroupsList } from 'store/userGroups';
import {
  Button,
  EmailInput,
  Grid,
  GridCell,
  RoleSelector,
  Text,
  TextInput,
  UserGroupSelector
} from 'shared';
import { InviteUsersFormData } from './InviteUsersModal';

export type InviteUsersFormDataUser = {
  email: Email;
  isSuperUser: boolean;
  userGroupId: string;
};

const copy = {
  inviteConflict: 'This user has already been invited'
};

type InviteUsersFormUsersProps = {
  hasInviteConflict: (email: string | number | undefined) => boolean;
};

const InviteUsersFormUsers = ({
  hasInviteConflict
}: InviteUsersFormUsersProps) => {
  const userGroups = useSelector(selectUserGroupsList);
  const [emailsToAdd, setEmailsToAdd] = useState<string | number | undefined>();
  const { fields, append, remove } = useFieldArray<InviteUsersFormData>({
    name: 'users'
  });

  const addEmails = () => {
    if (emailsToAdd) {
      const emails: InviteUsersFormDataUser[] = emailsToAdd
        .toString()
        .trim()
        .split(/[\s,]+/)
        .map(e => ({
          email: e,
          isSuperUser: false,
          userGroupId: get(userGroups, [0, 'id'])
        }));

      append(emails);
      setEmailsToAdd(undefined);
    }
  };

  return (
    <>
      {fields.map((field, i) => (
        <div className="stack-s" key={field.id}>
          <Grid gutters="xs">
            <GridCell width="fill">
              <Controller<InviteUsersFormData, `users.${number}.email`>
                name={`users.${i}.email`}
                defaultValue={field.email}
                render={({ field: { value, onChange } }) => (
                  <>
                    <EmailInput size="s" value={value} onBlur={onChange} />
                    {hasInviteConflict(value) ? (
                      <Text color="danger" size="xs">
                        {copy.inviteConflict}
                      </Text>
                    ) : null}
                  </>
                )}
              />
            </GridCell>
            <GridCell width="1/4">
              <Controller<InviteUsersFormData, `users.${number}.isSuperUser`>
                name={`users.${i}.isSuperUser`}
                defaultValue={field.isSuperUser}
                render={({ field: { value, onChange } }) => (
                  <RoleSelector size="s" value={value} onChange={onChange} />
                )}
              />
            </GridCell>
            <GridCell width="1/4">
              <Controller<InviteUsersFormData, `users.${number}.userGroupId`>
                name={`users.${i}.userGroupId`}
                defaultValue={field.userGroupId}
                render={({ field: { value, onChange } }) => (
                  <UserGroupSelector size="s" value={value} onBlur={onChange} />
                )}
              />
            </GridCell>
            <GridCell width="fixed">
              <Button
                icon="cross"
                appearance="silent"
                size="s"
                action={() => remove(i)}
              />
            </GridCell>
          </Grid>
        </div>
      ))}
      <Grid gutters="xs">
        <GridCell width="fill">
          <TextInput
            size="s"
            placeholder={`Enter emails here separated by a comma ","`}
            value={emailsToAdd}
            onBlur={setEmailsToAdd}
          />
        </GridCell>
        <GridCell width="fixed">
          <Button label="Add" size="s" action={addEmails} />
        </GridCell>
      </Grid>
    </>
  );
};

export default InviteUsersFormUsers;
